import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Video, X } from "lucide-react";

export default function VideoRoomLiveBanner() {
  const nav = useNavigate();
  const [live, setLive] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return new Set(JSON.parse(sessionStorage.getItem("cq_vroom_dismissed") || "[]")); }
    catch { return new Set(); }
  });
  const dismissedRef = useRef(dismissed);
  dismissedRef.current = dismissed;

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/warroom/live");
        if (!cancelled) setLive(data?.items || []);
      } catch { /* silent */ }
    };
    tick();
    const t = setInterval(tick, 6000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Show only rooms the user hasn't dismissed this session
  const toShow = live.filter((r) => !dismissed.has(r.slug));
  if (toShow.length === 0) return null;

  const names = toShow.flatMap((r) => r.participants || []);
  const nameLabel = names.length <= 2 ? names.join(" & ") : `${names.slice(0, 2).join(", ")} +${names.length - 2}`;

  const join = () => {
    if (toShow.length === 1) nav(`/admin/warroom?join=${encodeURIComponent(toShow[0].slug)}`);
    else nav("/admin/warroom");
  };

  const dismiss = () => {
    const next = new Set(dismissed);
    live.forEach((r) => next.add(r.slug));
    setDismissed(next);
    try { sessionStorage.setItem("cq_vroom_dismissed", JSON.stringify([...next])); } catch {}
  };

  return (
    <div className="mb-4 flex items-center gap-3 rounded-xl bg-emerald-600 text-white px-4 py-2.5 shadow-sm" data-testid="video-room-live-banner">
      <span className="relative flex h-2.5 w-2.5 shrink-0" aria-label="live">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-70" />
        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white" />
      </span>
      <Video size={18} className="shrink-0" />
      <p className="flex-1 min-w-0 text-sm font-bold truncate">
        {toShow.length === 1 ? "A Video Room is live" : `${toShow.length} Video Rooms are live`}
        {names.length > 0 && <span className="font-normal opacity-90"> — {nameLabel} {names.length === 1 ? "is" : "are"} on</span>}
      </p>
      <button onClick={join} className="shrink-0 bg-white text-emerald-700 font-black text-sm rounded-lg px-4 py-1.5 hover:bg-emerald-50 transition" data-testid="video-room-banner-join">
        Join
      </button>
      <button onClick={dismiss} className="shrink-0 text-white/80 hover:text-white" data-testid="video-room-banner-dismiss" aria-label="Dismiss">
        <X size={18} />
      </button>
    </div>
  );
}
